// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCompare.test.tsx
//
// SPIKE (side-by-side version compare). The OTHER version of a comparison: a
// second artwork pane beside the review's own, showing another review of the
// same chat with that review's pins. Read-only -- nothing is judged or
// commented here; the findings column stays the open review's.
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";
import { CircleNotch } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { orderPins, type PinOrderOptions } from "./pinGroups";
import { useAskPunttReview } from "./useAskPunttReview";
import { comparePinTone } from "./versionChanges";

import {
  AskPunttArtworkViewer,
  type AssetViewerPaneHandle,
} from "../../components/ask-puntt-review";
import { mapReviewFilesToAssets } from "../../components/ask-puntt-review/mapReviewAssets";
import { reviewCommentGroup } from "../../utils/visibleReviewComments";

/** The id the open review's selected finding is drawn under on the other version. */
export const COMPARE_GHOST_PREFIX = "compare-ghost-";

/**
 * Which pin the other version shows for the finding selected on the open one.
 *
 * The same finding, when the open review linked it to a comment of that
 * version (`earlierComment`); otherwise a copy of the selected finding at its
 * own place, so both panes move to the same area even when the other version
 * raised nothing there.
 */
export function comparedFocus(
  selected: AskPunttReviewComment | null,
  otherReviewId: string,
  otherComments: readonly AskPunttReviewComment[],
): { activeId: string | null; ghost: AskPunttReviewComment | null } {
  if (!selected) return { activeId: null, ghost: null };
  // The very pin is already drawn here (a changed window, shown on both
  // versions under one id): point at it, do not copy it.
  if (otherComments.some((comment) => comment._id === selected._id)) {
    return { activeId: selected._id, ghost: null };
  }
  const earlier = selected.earlierComment;
  if (
    earlier?.reviewId === otherReviewId &&
    otherComments.some((comment) => comment._id === earlier.commentId)
  ) {
    return { activeId: earlier.commentId, ghost: null };
  }
  const ghost = { ...selected, _id: `${COMPARE_GHOST_PREFIX}${selected._id}` };
  return { activeId: ghost._id, ghost };
}

type AskV2ReviewCompareProps = {
  /** The review shown here. Its picker is in the compare bar. */
  reviewId: string;
  /** The finding selected in the open review, or null. */
  selectedComment: AskPunttReviewComment | null;
  /** The number the open review prints for the selected finding. */
  selectedNumber?: number;
  currentUserId?: string;
  /**
   * The changed windows between the two versions, drawn here INSTEAD of this
   * version's own findings, each under its window number.
   */
  changes?: {
    pins: readonly AskPunttReviewComment[];
    numbers: Readonly<Record<string, number>>;
  };
  /** A pin clicked on this version: a changed window steps both viewers to it. */
  onPinClick?: (comment: { _id: string }) => void;
  /** Which side of the comparison this version is, to tone its change pins. */
  side?: "before" | "after" | null;
  /** Where the pane sits beside the open review; the earlier version is left. */
  placement?: "left" | "right";
  /**
   * The open review's pin view. This version's findings are hidden, folded
   * and numbered by it, so a finding carried across shows as one pin on both.
   */
  pinView?: Omit<PinOrderOptions, "summaries" | "isOpen">;
};

const AS_STORED: Omit<PinOrderOptions, "summaries" | "isOpen"> = {
  enabled: false,
  showMinor: true,
  grouping: false,
  groupBy: "block",
};

const isOpenHere = (comment: AskPunttReviewComment) =>
  reviewCommentGroup(comment) === "unresolved";

export function AskV2ReviewCompare({
  reviewId,
  selectedComment,
  selectedNumber,
  currentUserId,
  changes,
  onPinClick,
  side = null,
  placement = "left",
  pinView = AS_STORED,
}: AskV2ReviewCompareProps) {
  const { t } = useTranslation("ui");
  const query = useAskPunttReview(reviewId);
  const review = query.data?.review;
  const paneRef = useRef<AssetViewerPaneHandle>(null);
  const files = useMemo(
    () => mapReviewFilesToAssets(review?.files ?? []),
    [review?.files],
  );
  const live = useMemo(
    () =>
      changes
        ? [...changes.pins]
        : orderPins(
            (review?.comments ?? []).filter((comment) => !comment.isDeleted),
            {
              ...pinView,
              summaries: review?.groupSummaries,
              isOpen: isOpenHere,
            },
          ).ordered,
    [changes, pinView, review?.comments, review?.groupSummaries],
  );
  const focus = useMemo(
    () => comparedFocus(selectedComment, reviewId, live),
    [live, reviewId, selectedComment],
  );
  const comments = useMemo(
    () => (focus.ghost ? [...live, focus.ghost] : live),
    [focus.ghost, live],
  );
  // The other version's own pins are numbered in the open review's order; the
  // copied one carries the number the findings column shows for it.
  const numbers = useMemo(() => {
    const out: Record<string, number> = {};
    live.forEach((comment, index) => {
      out[comment._id] = changes?.numbers[comment._id] ?? index + 1;
    });
    if (focus.ghost && selectedNumber != null) {
      out[focus.ghost._id] = selectedNumber;
    }
    return out;
  }, [changes, focus.ghost, live, selectedNumber]);

  const page = selectedComment?.page_num;
  useEffect(() => {
    if (focus.activeId && typeof page === "number" && page > 0) {
      paneRef.current?.navigateToPage(page);
    }
  }, [focus.activeId, page]);

  return (
    <div
      className={`flex min-h-0 min-w-0 flex-1 flex-col border-puntt-neutral-gray-5 ${
        placement === "left" ? "border-r" : "border-l"
      }`}
      data-testid="ask-v2-review-compare"
      role="region"
      aria-label={t("askV2.review.compareOtherLabel")}
    >
      <div className="min-h-0 flex-1">
        {query.isLoading ? (
          <div
            className="flex h-full items-center justify-center"
            data-testid="ask-v2-review-compare-loading"
          >
            <CircleNotch
              size={24}
              weight="bold"
              className="animate-spin text-puntt-neutral-gray-9"
              aria-hidden="true"
            />
          </div>
        ) : !review ? (
          <div
            className="flex h-full items-center justify-center px-6 text-center text-sm text-puntt-neutral-gray-10"
            data-testid="ask-v2-review-compare-error"
          >
            {t("askV2.review.loadError")}
          </div>
        ) : (
          <AskPunttArtworkViewer
            ref={paneRef}
            reviewId={review.reviewId}
            title={review.title}
            files={files}
            comments={comments}
            commentNumbers={numbers}
            currentUserId={currentUserId}
            activeCommentId={focus.activeId}
            onCommentClick={onPinClick}
            pinTone={comparePinTone(side)}
          />
        )}
      </div>
    </div>
  );
}
